import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material'
import {
  Menu as MenuIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  AdminPanelSettings,
  Palette as PaletteIcon,
  Receipt as ReceiptIcon,
  BarChart as BarChartIcon,
  Storage as StorageIcon,
  People as PeopleIcon,
  MenuBook as MenuBookIcon,
  Feedback as FeedbackIcon,
  HomeOutlined as OverviewIcon,
  AccountTreeOutlined as ProjectsIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { NavigationProps } from '../types'
import ProductTitle from './ProductTitle'
import { getDocsSiteOrigin } from '../config'
import { useAuth } from '../contexts/AuthContext'
import { useRoleAssumption } from '../contexts/RoleAssumptionContext'

interface MenuItem {
  text: string
  path: string
  icon: React.ReactNode
  disabled?: boolean
}

const Navigation: React.FC<NavigationProps> = () => {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false)
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, isAuthenticated, logout } = useAuth()
  const { isRoleAssumed, assumedUser } = useRoleAssumption()

  const menuItems: MenuItem[] = []

  // Only add menu items if user is authenticated
  if (isAuthenticated) {
    // Billing remains visible only to joram in dashboard navigation.
    if (user?.login === 'joram') {
      menuItems.push({
        text: 'Billing',
        path: '/billing',
        icon: <ReceiptIcon />
      })
    }

  }

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen)
  }

  const handleNavigation = (path: string): void => {
    navigate(path)
    if (isMobile) {
      setMobileOpen(false)
    }
  }

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setUserMenuAnchor(event.currentTarget)
  }

  const handleUserMenuClose = (): void => {
    setUserMenuAnchor(null)
  }

  const handleLogout = (): void => {
    logout()
    handleUserMenuClose()
    navigate('/login')
  }

  const isActive = (path: string): boolean => {
    // Handle repositories tab matching
    if (path.includes('?tab=2')) {
      return location.pathname + location.search === path
    }
    return location.pathname === path
  }

  const drawer = (
    <Box sx={{ width: 250, p: 2 }}>
      <Box 
        onClick={() => {
          navigate('/')
          handleDrawerToggle()
        }}
        sx={{ 
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        <ProductTitle 
          size="medium" 
          centered={false}
        />
      </Box>
      {isAuthenticated && (
        <List sx={{ mb: 1 }}>
          {[
            { text: 'Home', path: '/', icon: <OverviewIcon /> },
            { text: 'Projects', path: '/projects', icon: <ProjectsIcon /> },
            { text: 'Databases', path: '/databases', icon: <StorageIcon /> },
          ].map((item) => (
            <ListItem
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              sx={{ cursor: 'pointer', borderRadius: 1 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
          {[
            { text: 'Users', path: '/databases/users', icon: <PeopleIcon /> },
          ].map((item) => (
            <ListItem
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              sx={{ cursor: 'pointer', borderRadius: 1 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
      )}
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            onClick={() => !item.disabled && handleNavigation(item.path)}
            sx={{
              bgcolor: isActive(item.path) ? 'var(--vs-surface-raised)' : 'transparent',
              color: isActive(item.path) ? 'white' : item.disabled ? 'text.disabled' : 'inherit',
              cursor: item.disabled ? 'default' : 'pointer',
              opacity: item.disabled ? 0.5 : 1,
              '&:hover': {
                bgcolor: item.disabled ? 'transparent' : (isActive(item.path) ? 'var(--vs-border)' : 'action.hover'),
              },
            }}
          >
            <ListItemIcon sx={{ color: isActive(item.path) ? 'white' : item.disabled ? 'text.disabled' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  )

  return (
    <>
      {isRoleAssumed && (
        <Box
          sx={{
            bgcolor: '#8B0000', // Dark red
            color: 'white',
            py: 1,
            px: 2,
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '1.1rem',
          }}
        >
          ⚠️ WARNING: ROLE ASSUMED - Acting as {assumedUser?.github_username} (ID: {assumedUser?.id})
        </Box>
      )}
      {/* Mobile-only header: menu + user (desktop uses sidebar with logo + user menu) */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <AppBar
          position="static"
          sx={{
            bgcolor: isRoleAssumed ? '#8B0000' : undefined,
          }}
        >
          <Toolbar sx={{ px: 2, minHeight: 48 }}>
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            {/* User menu (mobile) */}
            {isAuthenticated && user && (
              <>
                <IconButton
                  color="inherit"
                  aria-label="user-profile"
                  onClick={handleUserMenuOpen}
                  sx={{ ml: 2 }}
                >
                  {user.avatar_url ? (
                    <Avatar 
                      src={user.avatar_url} 
                      alt={user.name || user.login}
                      sx={{ width: 32, height: 32 }}
                    />
                  ) : (
                    <AccountIcon />
                  )}
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      bgcolor: '#001A36',
                      border: '1px solid #002F61',
                      borderRadius: '12px',
                      minWidth: 176,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      mt: 0.5,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem',
                        borderRadius: '6px',
                        mx: 0.5,
                        px: 1.25,
                        py: 0.75,
                        color: 'rgba(255,255,255,0.87)',
                        '& .MuiListItemIcon-root': { minWidth: 32, color: 'rgba(255,255,255,0.7)' },
                        '&:hover': { bgcolor: 'rgba(134,190,223,0.10)' },
                        '&.Mui-disabled': { opacity: 1 },
                      },
                      '& .MuiDivider-root': { borderColor: 'rgba(255,255,255,0.07)', mx: 1 },
                    },
                  }}
                >
                  <Box sx={{ px: 1.75, pt: 1.25, pb: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.52)', letterSpacing: '0.02em' }}>
                      {user.name || user.login}
                    </Typography>
                  </Box>
                  <Divider />
                  {user.login === 'joram' && (
                    <MenuItem
                      onClick={() => {
                        handleUserMenuClose()
                        window.open('https://admin.veilstream.com', '_blank', 'noopener,noreferrer')
                      }}
                    >
                      <ListItemIcon><AdminPanelSettings sx={{ fontSize: 18 }} /></ListItemIcon>
                      Admin
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings') }}>
                    <ListItemIcon><PaletteIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    Settings
                  </MenuItem>
                  <MenuItem onClick={() => { handleUserMenuClose(); navigate('/usage') }}>
                    <ListItemIcon><BarChartIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    Usage
                  </MenuItem>
                  <MenuItem onClick={() => { handleUserMenuClose(); navigate('/billing') }}>
                    <ListItemIcon><ReceiptIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    Billing
                  </MenuItem>
                  <MenuItem
                    component="a"
                    href={getDocsSiteOrigin()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleUserMenuClose}
                    sx={{ color: 'rgba(255,255,255,0.87) !important', textDecoration: 'none !important' }}
                  >
                    <ListItemIcon><MenuBookIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    Docs
                    <OpenInNewIcon sx={{ fontSize: 12, ml: 'auto', pl: 0.5, color: 'rgba(255,255,255,0.38)' }} />
                  </MenuItem>
                  <MenuItem onClick={() => { handleUserMenuClose(); navigate('/feedback') }}>
                    <ListItemIcon><FeedbackIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    Feedback
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon><LogoutIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </Toolbar>
        </AppBar>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  )
}

export default Navigation
